import React from "react";
import { CREATORS, STATS } from "../data/site";
import Counter from "./Counter";
import { Eye, Image as ImageIcon, User, ArrowRight } from "lucide-react";

const ICONS = { Eye, Image: ImageIcon, User };

/**
 * Creators — "Creators We've Worked With" section.
 * Typography rules (exact, no substitutions):
 *   - "Creators We've" pill   →  Karvest Trial
 *   - "Worked With" heading   →  Clash Display Medium (500)
 *   - Creator names           →  SF Pro Display Bold (700)
 *   - Subscriber counts       →  SF Pro Display Bold (700)
 *   - Stats numbers           →  SF Pro Display Bold (700)
 *   - Stats descriptions      →  SF Pro Display Medium (500)
 */

function StatPill({ Ic, value, suffix, label, className = "" }) {
  return (
    <div
      className={`reveal ${className} flex items-center justify-center gap-3 md:gap-5 px-4 md:px-6 py-3 md:py-4 rounded-[28px] md:rounded-[36px] border border-white/10 backdrop-blur-md transition-shadow duration-500`}
      style={{
        background:
          "linear-gradient(180deg, rgba(30, 27, 75, 0.55) 0%, rgba(15, 23, 42, 0.55) 100%)",
        boxShadow:
          "0 10px 40px rgba(0,0,0,0.35), inset 0 1px 0 rgba(255,255,255,0.03)",
      }}
    >
      <div className="shrink-0 w-11 h-11 md:w-[72px] md:h-[72px] rounded-2xl bg-[#4F46E5]/85 flex items-center justify-center border border-white/10 shadow-inner">
        <Ic
          className="w-5 h-5 md:w-9 md:h-9 text-white"
          strokeWidth={1.6}
        />
      </div>
      <div className="text-left leading-tight">
        <div
          className="text-white"
          style={{
            fontFamily: "'SF Pro Display',sans-serif",
            fontWeight: 700,
            fontSize: "clamp(20px, 3vw, 42px)",
            lineHeight: 1.05,
            letterSpacing: "-0.01em",
          }}
        >
          <Counter end={value} suffix={suffix} />
        </div>
        <div
          className="mt-0.5"
          style={{
            fontFamily: "'SF Pro Display',sans-serif",
            fontWeight: 500,
            fontSize: "clamp(10px, 1vw, 15px)",
            lineHeight: 1.25,
            color: "rgba(255,255,255,0.72)",
          }}
        >
          {label}
        </div>
      </div>
    </div>
  );
}

export default function Creators() {
  return (
    <section id="creators" className="relative py-6 md:py-14 -mt-24 md:-mt-14">
      <div className="max-w-6xl mx-auto px-5 md:px-8 text-center">
        {/* "Creators We've" pill — Karvest, slightly larger on desktop */}
        <div className="reveal">
          <span
            className="inline-flex items-center rounded-full"
            style={{
              padding: "12px 30px",
              border: "1px solid rgba(139,92,246,0.35)",
              backgroundColor: "rgba(15,23,42,0.6)",
              backdropFilter: "blur(8px)",
              WebkitBackdropFilter: "blur(8px)",
              color: "#E5E7EB",
              fontFamily: "'Karvest',sans-serif",
              fontWeight: 400,
              fontSize: "clamp(15px, 1.5vw, 22px)",
              letterSpacing: "0.01em",
              lineHeight: 1.1,
            }}
          >
            Creators We&apos;ve
          </span>
        </div>

        {/* "Worked With" heading — Clash Display Medium */}
        <h2
          className="reveal text-transparent bg-clip-text mt-4"
          style={{
            fontFamily: "'Clash Display',sans-serif",
            fontWeight: 500,
            fontSize: "clamp(44px, 8vw, 96px)",
            lineHeight: 0.95,
            letterSpacing: "-0.02em",
            backgroundImage:
              "linear-gradient(180deg,#EDE9FE 0%,#C4B5FD 60%,#8B5CF6 100%)",
            WebkitBackgroundClip: "text",
            backgroundClip: "text",
            margin: 0,
          }}
        >
          Worked With
        </h2>

        {/* Purple brushstroke underline */}
        <div className="reveal mx-auto mt-2 md:mt-3 w-44 sm:w-52 md:w-72 h-6 md:h-8">
          <img
            src="/assets/images/underline-draw.png"
            alt=""
            aria-hidden="true"
            className="w-full h-full object-contain select-none pointer-events-none"
            draggable="false"
          />
        </div>

        {/* Creators grid — Mobile: 3 cols × 4 rows, Desktop: 4 cols × 3 rows */}
        <div className="reveal grid grid-cols-3 md:grid-cols-4 gap-x-4 md:gap-x-6 gap-y-8 md:gap-y-10 mt-8 md:mt-14">
          {CREATORS.map((c) => (
            <div key={c.name} className="flex flex-col items-center group">
              <div className="w-[68px] h-[68px] sm:w-20 sm:h-20 md:w-28 md:h-28 rounded-full overflow-hidden ring-2 ring-white/10 shadow-[0_10px_30px_rgba(0,0,0,0.5)] transition-transform duration-300 group-hover:scale-105">
                <img
                  src={c.img}
                  alt={c.name}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
              <div
                className="mt-3 md:mt-4 text-white"
                style={{
                  fontFamily: "'SF Pro Display',sans-serif",
                  fontWeight: 700,
                  fontSize: "clamp(11px, 1.15vw, 16px)",
                  letterSpacing: "-0.005em",
                  lineHeight: 1.15,
                  textAlign: "center",
                }}
              >
                {c.name}
              </div>
              <div
                className="mt-1"
                style={{
                  fontFamily: "'SF Pro Display',sans-serif",
                  fontWeight: 700,
                  fontSize: "clamp(10px, 0.95vw, 14px)",
                  letterSpacing: "-0.005em",
                  lineHeight: 1.2,
                  color: "rgba(255,255,255,0.62)",
                  textAlign: "center",
                }}
              >
                {c.subs}
              </div>
            </div>
          ))}

          {/* + more button — fills the 4th slot of the last row on desktop; full-width row on mobile */}
          <div className="col-span-3 md:col-span-1 flex items-center justify-center mt-2 md:mt-0 md:pb-10">
            <button
              data-testid="creators-more-button"
              className="group inline-flex items-center gap-3 pl-5 pr-2 py-2 rounded-full bg-white/95 text-[#1e1b4b] shadow-[0_10px_30px_rgba(79,70,229,0.35)] hover:bg-white transition"
              style={{
                fontFamily: "'Clash Display',sans-serif",
                fontWeight: 500,
                fontSize: "14px",
                letterSpacing: "0.005em",
              }}
            >
              + more
              <span className="w-8 h-8 rounded-full bg-[#0B0A1F] text-white flex items-center justify-center transition-transform group-hover:translate-x-0.5">
                <ArrowRight className="w-4 h-4" />
              </span>
            </button>
          </div>
        </div>

        {/* Stats — 2 side-by-side then 1 below (both mobile and desktop) */}
        <div className="mt-16 md:mt-24 grid grid-cols-2 gap-3 md:gap-6 max-w-3xl mx-auto">
          <StatPill
            Ic={ICONS[STATS[0].icon]}
            value={STATS[0].value}
            suffix={STATS[0].suffix}
            label={STATS[0].label}
          />
          <StatPill
            Ic={ICONS[STATS[1].icon]}
            value={STATS[1].value}
            suffix={STATS[1].suffix}
            label={STATS[1].label}
          />
        </div>
        <div className="mt-3 md:mt-6 grid grid-cols-2 gap-3 md:gap-6 max-w-3xl mx-auto">
          <StatPill
            className="col-span-2 justify-self-center w-[calc(50%-6px)] md:w-[calc(50%-12px)]"
            Ic={ICONS[STATS[2].icon]}
            value={STATS[2].value}
            suffix={STATS[2].suffix}
            label={STATS[2].label}
          />
        </div>
      </div>
    </section>
  );
}
